import { Assignment as AssignmentIcon } from '@mui/icons-material';
import { faPlus, faSearch } from '@fortawesome/free-solid-svg-icons';
import { Button as FsButton, useModal } from '@farmshare/ui-components';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { map } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';

import {
  type Cutsheet,
  EnumCutsheetAnimalSpecies,
  EnumCutsheetQuestionnaireResponseType,
  ProducerJobByIdDocument,
  ProducerJobByIdQuery,
  SortFindManyCutsheetInput,
  SortFindManyCutsheetQuestionnaireResponseInput,
  useCutsheetCapabilityManyQuery,
  useCutsheetQuestionnaireResponseSearchQuery,
  useCutsheetQuestionnaireResponsesByUserQuery,
  useCutsheetSearchLazyQuery,
  useProcessorSchedulingCutsheetsPartialUpdateMutation,
} from 'lib/graphql';
import { userState } from 'state';
import { isBuyerOnly, isProcessor } from 'lib/userHelpers';

import {
  useCatalogState,
  useQuestionnaireState,
} from 'features/cutsheets/v2/hooks';
import { QuestionnaireWizard } from 'features/cutsheets/v2/components';
import { ConvertLegacyCutsheetModal } from 'features/cutsheets/v2/components/convert-legacy-cutsheet-modal';

import { SplitSlotDrawer } from './split-slot/split-slot-drawer';
import {
  getQuestionnaireCapabilityId,
  getQuestionnaireId,
} from './split-slot/utils';

type Job = NonNullable<ProducerJobByIdQuery['processorSchedulingById']>;
type AnimalHead = NonNullable<Job['animalHeads']>[number];
type CutsheetInfo = NonNullable<
  NonNullable<NonNullable<AnimalHead>['cutsheetInformation']>[number]
>;

interface ProducerQuestionnaireResponseDrawerProps {
  open: boolean;
  onClose: () => void;
  /**
   * Called after a response is successfully assigned. When omitted the drawer
   * falls back to refetching ProducerJobByIdDocument automatically.
   */
  onResponseAssigned?: () => void | Promise<void>;
  job: Job;
  cutsheetInfo: NonNullable<CutsheetInfo>;
}

export function ProducerQuestionnaireResponseDrawer({
  open,
  onClose,
  onResponseAssigned,
  job,
  cutsheetInfo,
}: ProducerQuestionnaireResponseDrawerProps) {
  const user = useRecoilValue(userState);
  const { info: openInfoModal, destroy: destroyInfoModal } = useModal();
  const [wizardOpen, setWizardOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [legacySearchOpen, setLegacySearchOpen] = useState(false);

  const vendorId = job.vendor?._id;
  const contactUserId = cutsheetInfo.contactUser?._id;
  const producerId = job.requestedBy?._id;
  const isSelf = contactUserId === user?._id;
  const buyerMode = user ? isBuyerOnly(user) : false;
  const showBuyerTab = !isSelf && !!contactUserId;
  const showProducerTab = buyerMode && !!producerId && producerId !== user?._id;
  const shopName = job.vendor?.shop_name || 'House';

  const questionnaireId = getQuestionnaireId(cutsheetInfo.questionnaire);
  const embeddedCapabilityId = getQuestionnaireCapabilityId(
    cutsheetInfo.questionnaire,
  );

  const { data: capabilityData } = useCutsheetCapabilityManyQuery({
    variables: {
      vendorId,
      species: job.animalSpecies as string,
      isActive: true,
    },
    skip: !vendorId,
  });

  const capabilityId =
    embeddedCapabilityId ??
    capabilityData?.CutsheetCapabilityByVendorAndAnimal?.[0]?._id ??
    undefined;

  const currentResponseId = (
    cutsheetInfo.questionnaireResponse as { _id?: string } | null | undefined
  )?._id;

  // ── Load existing responses ────────────────────────────────────────────────

  const {
    data: myResponsesData,
    loading: myLoading,
    refetch: refetchMyResponses,
  } = useCutsheetQuestionnaireResponsesByUserQuery({
    variables: {
      userId: user?._id,
      questionnaireId: questionnaireId ?? undefined,
    },
    skip: !user?._id || !questionnaireId,
    fetchPolicy: 'network-only',
  });

  const {
    data: buyerResponsesData,
    loading: buyerLoading,
    refetch: refetchBuyerResponses,
  } = useCutsheetQuestionnaireResponsesByUserQuery({
    variables: {
      userId: contactUserId,
      questionnaireId: questionnaireId ?? undefined,
    },
    skip: !showBuyerTab || !questionnaireId,
    fetchPolicy: 'network-only',
  });

  const { data: producerResponsesData, loading: producerLoading } =
    useCutsheetQuestionnaireResponsesByUserQuery({
      variables: {
        userId: producerId,
        questionnaireId: questionnaireId ?? undefined,
      },
      skip: !showProducerTab || !questionnaireId,
      fetchPolicy: 'network-only',
    });

  const { data: vendorResponsesData, loading: vendorLoading } =
    useCutsheetQuestionnaireResponseSearchQuery({
      variables: {
        vendorId,
        questionnaireId: questionnaireId ?? undefined,
        type: [EnumCutsheetQuestionnaireResponseType.Vendor],
        includeDeleted: false,
        sort: SortFindManyCutsheetQuestionnaireResponseInput.CreatedatDesc,
        page: 1,
        perPage: 50,
      },
      skip: !vendorId || !questionnaireId,
      fetchPolicy: 'network-only',
    });

  type ResponseItem = {
    _id: string;
    name?: string | null;
    createdAt?: string | null;
    selectedCuts?: Array<unknown> | null;
  };

  const myResponses = useMemo<ResponseItem[]>(
    () =>
      (myResponsesData?.cutsheetQuestionnaireResponsesByUser ?? []).filter(
        (r): r is NonNullable<typeof r> => Boolean(r),
      ),
    [myResponsesData],
  );

  const buyerResponses = useMemo<ResponseItem[]>(
    () =>
      (buyerResponsesData?.cutsheetQuestionnaireResponsesByUser ?? []).filter(
        (r): r is NonNullable<typeof r> => Boolean(r),
      ),
    [buyerResponsesData],
  );

  const producerResponses = useMemo<ResponseItem[]>(
    () =>
      (
        producerResponsesData?.cutsheetQuestionnaireResponsesByUser ?? []
      ).filter((r): r is NonNullable<typeof r> => Boolean(r)),
    [producerResponsesData],
  );

  const vendorResponses = useMemo<ResponseItem[]>(
    () =>
      (
        vendorResponsesData?.cutsheetQuestionnaireResponseSearch?.items ?? []
      ).filter((r): r is NonNullable<typeof r> => Boolean(r)),
    [vendorResponsesData],
  );

  const tabs = useMemo(() => {
    const t: {
      label: string;
      responses: ResponseItem[];
      loading: boolean;
    }[] = [{ label: 'Mine', responses: myResponses, loading: myLoading }];
    t.push({
      label: shopName,
      responses: vendorResponses,
      loading: vendorLoading,
    });
    if (showProducerTab) {
      t.push({
        label: "Producer's",
        responses: producerResponses,
        loading: producerLoading,
      });
    }
    if (showBuyerTab) {
      t.push({
        label: 'Buyer',
        responses: buyerResponses,
        loading: buyerLoading,
      });
    }
    return t;
  }, [
    myResponses,
    myLoading,
    vendorResponses,
    vendorLoading,
    shopName,
    producerResponses,
    producerLoading,
    showProducerTab,
    buyerResponses,
    buyerLoading,
    showBuyerTab,
  ]);

  const activeTabData = tabs[activeTab] ?? tabs[0];

  // ── Load questionnaire + catalog for the wizard ────────────────────────────

  const { questionnaire, loading: qLoading } = useQuestionnaireState({
    questionnaireId: questionnaireId ?? undefined,
    capabilityId,
    skipByIdQuery: true,
  });

  const { catalog, loading: catalogLoading } = useCatalogState({
    vendorId: vendorId ?? '',
    capabilityId: questionnaire?.capabilityId ?? '',
  });

  const wizardLoading = qLoading || catalogLoading;

  // ── Assign response to slot ────────────────────────────────────────────────

  const refetchJob = useMemo(
    () => ({ query: ProducerJobByIdDocument, variables: { id: job._id } }),
    [job._id],
  );

  const [updateCutsheets, { loading: saving }] =
    useProcessorSchedulingCutsheetsPartialUpdateMutation();

  const handleSelect = useCallback(
    async (responseId: string) => {
      if (saving) return;
      await updateCutsheets({
        variables: {
          processorSchedulingId: job._id,
          record: [
            { _id: cutsheetInfo._id, questionnaireResponse: responseId },
          ],
        },
        refetchQueries: onResponseAssigned ? [] : [refetchJob],
      });
      await onResponseAssigned?.();
      onClose();
    },
    [
      saving,
      updateCutsheets,
      job._id,
      cutsheetInfo._id,
      onResponseAssigned,
      refetchJob,
      onClose,
    ],
  );

  const handleWizardSave = useCallback(
    (responseId: string) => {
      refetchMyResponses();
      if (showBuyerTab) refetchBuyerResponses();
      setWizardOpen(false);
      handleSelect(responseId);
    },
    [refetchMyResponses, refetchBuyerResponses, showBuyerTab, handleSelect],
  );

  // ── Legacy cutsheet search + conversion ────────────────────────────────────

  const [searchLegacyCutsheets, legacyQuery] = useCutsheetSearchLazyQuery({
    fetchPolicy: 'network-only',
  });

  const legacyCutsheets = useMemo(
    () =>
      (legacyQuery.data?.cutsheetSearch?.items ?? []).filter(
        (c): c is NonNullable<typeof c> => Boolean(c),
      ),
    [legacyQuery.data],
  );

  const handleOpenLegacySearch = useCallback(() => {
    if (!vendorId || !contactUserId) return;
    setLegacySearchOpen(true);
    searchLegacyCutsheets({
      variables: {
        vendorId,
        buyerId: contactUserId,
        species: job.animalSpecies as unknown as EnumCutsheetAnimalSpecies,
        includeDeleted: false,
        sort: SortFindManyCutsheetInput.IdDesc,
        page: 1,
        perPage: 50,
      },
    });
  }, [vendorId, contactUserId, job.animalSpecies, searchLegacyCutsheets]);

  const handleConvertLegacyCutsheet = useCallback(
    (legacy: Cutsheet) => {
      openInfoModal({
        type: 'info',
        title: `Convert "${legacy.name}" to New Format`,
        size: 'xl',
        footer: null,
        body: (
          <ConvertLegacyCutsheetModal
            cutsheet={legacy}
            onClose={destroyInfoModal}
            onConverted={async (responseId) => {
              setLegacySearchOpen(false);
              await handleSelect(responseId);
            }}
          />
        ),
      });
    },
    [openInfoModal, destroyInfoModal, handleSelect],
  );

  const drawerTitle = currentResponseId ? 'Change Cutsheet' : 'Select Cutsheet';

  return (
    <>
      <SplitSlotDrawer open={open} title={drawerTitle} onClose={onClose}>
        <Stack sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
          {!questionnaireId && (
            <Alert severity="warning" sx={{ m: 2, flexShrink: 0 }}>
              No questionnaire assigned to this slot.
            </Alert>
          )}

          {questionnaireId && (
            <>
              {tabs.length > 1 && (
                <Tabs
                  value={activeTab}
                  onChange={(_, v) => setActiveTab(v)}
                  variant="fullWidth"
                  sx={{
                    px: 2,
                    borderBottom: 1,
                    borderColor: 'divider',
                    flexShrink: 0,
                  }}
                >
                  {map(tabs, (tab, i) => (
                    <Tab
                      key={i}
                      label={tab.label}
                      value={i}
                      sx={{ fontSize: '0.95rem', minHeight: 48 }}
                    />
                  ))}
                </Tabs>
              )}

              <Stack
                gap={1.5}
                sx={{ p: 2, flex: 1, minHeight: 0, overflowY: 'auto' }}
              >
                {activeTabData?.loading && (
                  <Box
                    sx={{ display: 'flex', justifyContent: 'center', py: 4 }}
                  >
                    <CircularProgress size={24} />
                  </Box>
                )}

                {!activeTabData?.loading &&
                  activeTabData?.responses.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                      <Typography
                        variant="h6"
                        color="text.secondary"
                        gutterBottom
                      >
                        No previous responses
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Fill out the questionnaire below to configure your
                        cutsheet.
                      </Typography>
                    </Box>
                  )}

                {!activeTabData?.loading &&
                  map(activeTabData?.responses, (response) => {
                    if (!response) return null;
                    const isSelected = response._id === currentResponseId;
                    return (
                      <Card
                        key={response._id}
                        variant="outlined"
                        sx={{
                          flexShrink: 0,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          border: isSelected ? '2px solid' : '1px solid',
                          borderColor: isSelected ? 'success.main' : 'divider',
                          bgcolor: isSelected
                            ? 'rgba(46, 125, 50, 0.06)'
                            : 'background.paper',
                          '&:hover': { boxShadow: 2 },
                        }}
                      >
                        <CardHeader
                          avatar={
                            <AssignmentIcon
                              color={isSelected ? 'success' : 'action'}
                            />
                          }
                          title={response.name}
                          subheader={
                            isSelected ? (
                              <Chip
                                label="Selected"
                                size="small"
                                color="success"
                                sx={{ mt: 0.5 }}
                              />
                            ) : undefined
                          }
                          titleTypographyProps={{
                            variant: 'subtitle2',
                            fontWeight: 600,
                          }}
                          sx={{ pb: 0 }}
                        />
                        <CardContent sx={{ pt: 1 }}>
                          <Stack spacing={1}>
                            {response.createdAt && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Saved{' '}
                                {format(
                                  new Date(response.createdAt as string),
                                  'MMM d, yyyy',
                                )}
                              </Typography>
                            )}
                            {(response.selectedCuts?.length ?? 0) > 0 && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {response.selectedCuts!.length} cut
                                {response.selectedCuts!.length !== 1
                                  ? 's'
                                  : ''}{' '}
                                selected
                              </Typography>
                            )}
                          </Stack>
                        </CardContent>
                        <CardActions sx={{ px: 2, pb: 1.5, pt: 0 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            disabled={saving}
                            onClick={() => handleSelect(response._id)}
                            sx={{ textTransform: 'none', flex: 1 }}
                          >
                            {isSelected ? 'Keep this response' : 'Select'}
                          </Button>
                        </CardActions>
                      </Card>
                    );
                  })}
              </Stack>
            </>
          )}
        </Stack>

        {/* Sticky footer — Fill Out New Questionnaire */}
        {questionnaireId && (
          <Box
            sx={{
              p: 2,
              borderTop: 1,
              borderColor: 'divider',
              flexShrink: 0,
            }}
          >
            <FsButton
              icon={faPlus}
              content={
                wizardLoading
                  ? 'Loading Cutsheet Form Template...'
                  : 'Fill Out New Cutsheet'
              }
              variant="primary"
              onClick={() => setWizardOpen(true)}
              sx={{ width: '100%' }}
            />
            {isProcessor(user) && (
              <FsButton
                icon={faSearch}
                content="Find Legacy Cutsheets"
                variant="outline-primary"
                disabled={!vendorId || !contactUserId}
                onClick={handleOpenLegacySearch}
                sx={{ width: '100%', mt: 1 }}
              />
            )}
            {saving && (
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="center"
                gap={1}
                sx={{ mt: 1 }}
              >
                <CircularProgress size={16} />
                <Typography variant="caption">Assigning response...</Typography>
              </Stack>
            )}
          </Box>
        )}
      </SplitSlotDrawer>

      {/* Legacy cutsheet search drawer */}
      <SplitSlotDrawer
        open={legacySearchOpen}
        title="Find Legacy Cutsheets"
        onClose={() => setLegacySearchOpen(false)}
      >
        <Stack sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <Stack
            gap={1.5}
            sx={{ p: 2, flex: 1, minHeight: 0, overflowY: 'auto' }}
          >
            {legacyQuery.loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            )}

            {!legacyQuery.loading && legacyCutsheets.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No legacy cutsheets found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  There are no legacy cutsheets to convert for this contact.
                </Typography>
              </Box>
            )}

            {!legacyQuery.loading &&
              map(legacyCutsheets, (legacy) => (
                <Card
                  key={legacy._id}
                  variant="outlined"
                  sx={{ flexShrink: 0 }}
                >
                  <CardHeader
                    title={legacy.name ?? 'Unnamed cutsheet'}
                    titleTypographyProps={{
                      variant: 'subtitle2',
                      fontWeight: 600,
                    }}
                    sx={{ pb: 0 }}
                  />
                  <CardContent sx={{ pt: 1 }}>
                    <Stack spacing={0.5}>
                      {(legacy.animalSpecies ||
                        legacy.inspectionLevels?.[0]) && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ textTransform: 'capitalize' }}
                        >
                          {[
                            legacy.animalSpecies
                              ? String(legacy.animalSpecies).toLowerCase()
                              : null,
                            legacy.inspectionLevels?.[0]
                              ? String(legacy.inspectionLevels[0]).toLowerCase()
                              : null,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </Typography>
                      )}
                      {legacy.lastUsedDate && (
                        <Typography variant="caption" color="text.secondary">
                          Last used{' '}
                          {format(
                            new Date(legacy.lastUsedDate as string),
                            'MMM d, yyyy',
                          )}
                        </Typography>
                      )}
                    </Stack>
                  </CardContent>
                  <CardActions sx={{ px: 2, pb: 1.5, pt: 0 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() =>
                        handleConvertLegacyCutsheet(legacy as Cutsheet)
                      }
                      sx={{ textTransform: 'none', flex: 1 }}
                    >
                      Convert
                    </Button>
                  </CardActions>
                </Card>
              ))}
          </Stack>
        </Stack>
      </SplitSlotDrawer>

      {/* Questionnaire wizard dialog */}
      {questionnaire && catalog && (
        <QuestionnaireWizard
          open={wizardOpen}
          onClose={() => setWizardOpen(false)}
          onSave={handleWizardSave}
          questionnaire={questionnaire}
          catalog={catalog}
          isProcessor={user ? isProcessor(user) : false}
          producerId={job.requestedBy?._id ?? undefined}
          buyerId={!isSelf ? (contactUserId ?? undefined) : undefined}
          cutsheetType={user && isBuyerOnly(user) ? 'user' : undefined}
        />
      )}
    </>
  );
}
